import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ExclamationTriangleIcon, TrashIcon } from "@radix-ui/react-icons"
import React from "react"
import { useDeleteImageMutation } from "../../../query/shopDataQuery"
import { useUrlImageQuery } from "../../../query/urlImageQuery"

type DeleteImageModalProps = {
  isOpen: boolean
  imageIndex: number
  imagePath: string
  onClose: () => void
}

export const DeleteImageModal: React.FC<DeleteImageModalProps> = ({
  isOpen,
  imageIndex,
  imagePath,
  onClose,
}) => {
  const { urlImage, pendingUrlImage, errorUrlImage } =
    useUrlImageQuery(imagePath)

  const {
    deleteImageMutation,
    isDeletingImage,
    deleteImageError,
    isDeleteError,
  } = useDeleteImageMutation()

  const handleConfirm = () => {
    deleteImageMutation(
      { imageIndex },
      {
        onSuccess: () => {
          onClose()
        },
      }
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center text-red-600 dark:text-red-400">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            Supprimer l'image
          </DialogTitle>
          <DialogDescription>
            Êtes-vous sûr de vouloir supprimer cette image de la galerie ? Cette
            action est irréversible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Aperçu de l'image à supprimer */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Image à supprimer :
            </p>
            <div className="relative">
              <div className="aspect-[4/3] w-full max-w-xs mx-auto overflow-hidden rounded-lg border-2 border-red-200 dark:border-red-800">
                {pendingUrlImage ? (
                  <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Chargement...
                      </p>
                    </div>
                  </div>
                ) : errorUrlImage || !urlImage ? (
                  <div className="w-full h-full flex items-center justify-center bg-red-50 dark:bg-red-900/20">
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <span className="text-red-500 text-lg">⚠</span>
                      </div>
                      <p className="text-xs text-red-600 dark:text-red-400 text-center px-2">
                        Erreur de chargement
                      </p>
                    </div>
                  </div>
                ) : (
                  <img
                    src={urlImage}
                    alt={`Image ${imageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Badge avec numéro de l'image */}
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                #{imageIndex + 1}
              </div>
            </div>
          </div>

          {/* Erreur de suppression */}
          {isDeleteError && deleteImageError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    Erreur de suppression
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {deleteImageError?.message ||
                      "Impossible de supprimer l'image. Veuillez réessayer."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Avertissement */}
          {!isDeleteError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    Attention
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    L'image sera définitivement supprimée de votre galerie et ne
                    pourra pas être récupérée.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-start pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isDeletingImage}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isDeletingImage}
            className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
          >
            {isDeletingImage ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Suppression...
              </>
            ) : (
              <>
                <TrashIcon className="h-4 w-4 mr-2" />
                Supprimer définitivement
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
